import { cn } from '@/lib/utils'
import * as React from 'react'
import { Input, type InputProps } from './ui/input'

type CamposTextField = {
    /**
     * Rótulo do campo. Fica dentro da caixa, acima do valor.
     */
    label?: React.ReactNode
    /** Texto auxiliar abaixo do campo. O erro tem precedência sobre ele. */
    helperText?: React.ReactNode
    /**
     * Mensagem de erro (string) ou apenas a marcação de inválido (boolean).
     * Espelha o par `error` + `helperText` em uma prop só.
     */
    error?: React.ReactNode | boolean
    /** Só desenha o asterisco no rótulo — não valida nada. */
    required?: boolean
    /** Não reserva a linha auxiliar abaixo do campo — para layouts compactos. */
    unHelperText?: boolean
    /** Classe do grupo (largura, coluna). Para a caixa do campo, use `inputClassName`. */
    className?: string
    /** Classe da caixa do campo — é o `className` do `Input`. */
    inputClassName?: string
}

/** `Omit` sobre união colapsa nas props comuns — o condicional força a distribuição. */
type SemClassName<T> = T extends unknown ? Omit<T, 'className'> : never

export type TextFieldProps = SemClassName<InputProps> & CamposTextField

/**
 * Rótulo + campo + linha auxiliar.
 *
 * A tipografia acompanha a do `FormFieldSelect`, para que um select e um campo
 * de texto lado a lado fiquem alinhados.
 *
 * `onClick` cai no root do campo (rótulo, adornos e sobra da caixa), não só no
 * controle. Ver o JSDoc da prop em `ui/input`.
 */
export const TextField = React.forwardRef<
    HTMLInputElement | HTMLTextAreaElement,
    TextFieldProps
>(
    (
        {
            label,
            helperText,
            error,
            required,
            unHelperText,
            className,
            inputClassName,
            id,
            size = 'medium',
            ...props
        },
        ref,
    ) => {
        const idGerado = React.useId()
        const idCampo = id ?? idGerado
        const idAuxiliar = `${idCampo}-auxiliar`

        const invalido = error !== undefined && error !== false
        const mensagemErro = typeof error === 'boolean' ? undefined : error
        const auxiliar = mensagemErro ?? helperText

        const temRotulo = label !== undefined && label !== ''
        const conteudoRotulo = temRotulo && (
            <>
                {label}
                {required && <span aria-hidden="true"> *</span>}
            </>
        )

        return (
            <div className={cn('flex w-full flex-col gap-1', className)}>
                <Input
                    {...(props as InputProps)}
                    ref={ref}
                    id={idCampo}
                    size={size}
                    label={conteudoRotulo || undefined}
                    className={cn(
                        // Sem edição não há foco a sinalizar — o anel do
                        // `outlined` só confundiria com um campo editável.
                        props.readOnly &&
                            'focus-within:border-input focus-within:ring-0',
                        // Alça de resize desalinha a caixa do resto do
                        // formulário; a altura é do layout, não do usuário.
                        '[&_textarea]:resize-none',
                        inputClassName,
                    )}
                    aria-invalid={invalido || undefined}
                    aria-required={required || undefined}
                    aria-describedby={auxiliar ? idAuxiliar : undefined}
                />
                {(!unHelperText || !!mensagemErro) && (
                    <span
                        id={idAuxiliar}
                        className={cn(
                            'px-3 text-xs',
                            invalido
                                ? 'text-destructive'
                                : 'text-muted-foreground',
                        )}
                    >
                        {auxiliar ?? ' '}
                    </span>
                )}
            </div>
        )
    },
)
TextField.displayName = 'TextField'
